import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, Paper, Snackbar, Switch, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import DownloadIcon from '@mui/icons-material/Download';
import EmailTemplateEditor from './EmailTemplateEditor';
import { getCurrentUser } from '../auth/LoginButton';
import { getUserRoles } from '../rbac/roles';
import {
  adoptEmailTemplate, formatBytes, formatWhen, getAdoptableTemplates, getTemplateCatalog,
  getTemplateHealth, isTemplateAdmin, listEmailTemplates, statusLabel, syncLabel,
  toTemplateApiError, describeUsage, PURPOSE_LABEL, STATUS_COLOR,
  type AdoptCandidate, type AdoptInspection, type Purpose, type SetupState, type TemplateCatalog,
  type TemplateHealth, type TemplatePublic,
} from '../services/email-templates-api';

/**
 * Email Templates - the SendGrid dynamic templates SIMS manages, one purpose per screen.
 *
 * The screen is always opened for ONE purpose, and lists, creates and adopts only that purpose.
 * Only one purpose has a screen:
 *   drip_layout -> "Drip Layout Template", a sub-tab of Drip Campaign (Survey.Admin, Drip.Manager)
 * so a Campaign Manager never sees the drip layout. Campaign templates have no screen: staff create
 * new dynamic templates in SendGrid, and New Campaign's pickers list them from there. The server
 * gates each template by its purpose on top of this (PURPOSE_ROLES).
 *
 * Staff write a template here, preview it with synthetic data and publish; SIMS creates the
 * SendGrid template or a new version of it, keeps the history and can roll back. The SendGrid
 * account is SHARED with other college systems (the new-student password emails, the website-lead
 * notifications), so this list holds only templates SIMS created or adopted. Everything else in
 * the account is never listed. A Survey.Admin can adopt a template SIMS already uses; adopting
 * only records it, nothing is written to SendGrid.
 *
 * Nothing here shows a campaign title or a recipient: the server never sends them (graduate
 * campaigns are titled with the graduate's name), only counts.
 *
 * Layout tiers (standing rule - separate views, never one fluid rule): the base styles are the
 * laptop; from 1800px the toolbar text gets room; from 2400px (the 34-inch ultrawide) the table
 * spacing opens up. Rules inside a media query never reach the laptop.
 */

const WIDE = '@media (min-width: 1800px)';
const ULTRAWIDE = '@media (min-width: 2400px)';

const SHRINKS = { minWidth: 0 };

/** The purposes that have a screen. Campaign templates are made in SendGrid itself. */
type ScreenPurpose = Extract<Purpose, 'drip_layout'>;

/** What each purpose's screen is called, and the sentence that says what it holds. */
const SCREEN: Record<ScreenPurpose, { heading: string; intro: string; noun: string; adoptFrom: string }> = {
  drip_layout: {
    heading: 'Drip Layout Template',
    intro: 'The SendGrid layout every drip email is sent in: the frame around each message, with the '
      + 'unsubscribe link and the college address.',
    noun: 'drip layout',
    adoptFrom: "SIMS's drip entries or the drip default setting send with",
  },
};

const HEAD = {
  color: 'var(--text-primary)',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  [ULTRAWIDE]: { px: 2.5, fontSize: '0.95rem' },
};
const CELL = {
  color: 'var(--text-secondary)',
  fontSize: '0.8rem',
  overflowWrap: 'anywhere',
  [ULTRAWIDE]: { fontSize: '0.9rem', px: 2.5, py: 1.25 },
};
const MONO = { fontFamily: 'Consolas, "Courier New", monospace' };

const INTRO = {
  color: 'var(--text-secondary)',
  maxWidth: 820,
  [WIDE]: { maxWidth: 1100 },
};

function copy(text: string | null | undefined) {
  if (!text) return;
  try { void navigator.clipboard?.writeText(text); } catch { /* clipboard blocked: nothing to do */ }
}

function IdCell({ id }: { id: string | null }) {
  if (!id) return <>—</>;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ...SHRINKS }}>
      <Box component="span" sx={{ ...MONO, fontSize: '0.75rem', overflowWrap: 'anywhere' }}>{id}</Box>
      <Tooltip title="Copy the SendGrid id">
        <IconButton size="small" aria-label="Copy the SendGrid id"
                    onClick={(e) => { e.stopPropagation(); copy(id); }}>
          <ContentCopyIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

// ── SendGrid health (Survey.Admin) ──────────────────────────────────────────────────────────────

function HealthDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [health, setHealth] = useState<TemplateHealth | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let live = true;
    setLoading(true); setError(null); setHealth(null);
    getTemplateHealth()
      .then((h) => { if (live) setHealth(h); })
      .catch((e) => { if (live) setError(toTemplateApiError(e, 'SendGrid could not be checked.').error); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [open]);

  const s = health?.scopes;
  const b = health?.budget;
  const row = (label: string, value: React.ReactNode) => (
    <TableRow>
      <TableCell sx={{ ...HEAD, fontSize: '0.85rem', width: 220 }}>{label}</TableCell>
      <TableCell sx={CELL}>{value}</TableCell>
    </TableRow>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>SendGrid check</DialogTitle>
      <DialogContent dividers>
        {loading && <Box sx={{ textAlign: 'center', p: 3 }}><CircularProgress /></Box>}
        {error && <Alert severity="error">{error}</Alert>}
        {health && (
          <TableContainer sx={SHRINKS}>
            <Table size="small">
              <TableBody>
                {row('Template key', health.templatesKey === 'set'
                  ? 'Set (SENDGRID_TEMPLATES_API_KEY)' : 'Missing: template editing is not set up yet')}
                {row('Sending key', health.mailKey === 'set' ? 'Set (SENDGRID_API_KEY)' : 'Missing: test sends cannot work')}
                {row('Key access', !s ? '—' : s.error
                  ? `Not checked: ${s.error}`
                  : s.ok ? 'Exactly the access SIMS allows'
                    : [s.missing.length ? `Missing: ${s.missing.join(', ')}` : '',
                      s.forbidden.length ? `More than SIMS allows: ${s.forbidden.join(', ')}` : '',
                      s.unexpected.length ? `Not expected: ${s.unexpected.join(', ')}` : '']
                      .filter(Boolean).join(' · '))}
                {row('Shared account budget', !b ? '—' : b.error
                  ? `Not counted: ${b.error}`
                  : `${b.slots ?? '?'} of ${b.limit} slots used (templates plus versions${b.versionsKnown === false
                    ? '; versions not reported, so templates only' : ''}${b.complete === false ? '; listing incomplete' : ''}). `
                    + `SIMS keeps ${b.reserve} free for the other systems. ${b.templates ?? '?'} templates in the account.`)}
                {row('SIMS-created templates', !b ? '—' : `${b.simsTemplates ?? '?'} of ${b.simsCap}`)}
                {row('Checked', formatWhen(b?.checkedAt))}
                {row('Never written by SIMS', (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    {health.denylist.map((id) => <Box key={id} component="span" sx={{ ...MONO, fontSize: '0.75rem' }}>{id}</Box>)}
                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                      Other systems' templates (new-student credentials and orientation) and the graduate survey.
                    </Typography>
                  </Box>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

// ── adopt an existing template (Survey.Admin; read + record only) ───────────────────────────────

function AdoptDialog({ open, purpose, setup, onClose, onAdopted }: {
  open: boolean;
  /** Only candidates SIMS already sends with as this purpose are offered, and only as this purpose. */
  purpose: ScreenPurpose;
  setup: SetupState;
  onClose: () => void;
  onAdopted: (message: string) => void;
}) {
  const [candidates, setCandidates] = useState<AdoptCandidate[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<{ sgTemplateId: string; purpose: Purpose } | null>(null);
  const [inspection, setInspection] = useState<AdoptInspection | null>(null);
  const [inspecting, setInspecting] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const loadCandidates = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      // The server answers every candidate with the purposes it is used as; this screen offers only
      // its own, so a template only campaigns send with never appears on the drip layout screen.
      setCandidates((await getAdoptableTemplates()).filter((c) => c.allowedPurposes.includes(purpose)));
    } catch (e) {
      setCandidates(null);
      setError(toTemplateApiError(e, 'The templates SIMS uses could not be listed.').error);
    } finally {
      setLoading(false);
    }
  }, [purpose]);

  useEffect(() => {
    if (!open) return;
    setPicked(null); setInspection(null); setName(''); setError(null);
    void loadCandidates();
  }, [open, loadCandidates]);

  const inspect = async (sgTemplateId: string, purpose: Purpose) => {
    setPicked({ sgTemplateId, purpose });
    setInspection(null); setError(null); setInspecting(true);
    try {
      const r = await adoptEmailTemplate({ sgTemplateId, purpose, confirm: false });
      setInspection(r as AdoptInspection);
    } catch (e) {
      setError(toTemplateApiError(e, 'That template could not be inspected.').error);
    } finally {
      setInspecting(false);
    }
  };

  const adopt = async () => {
    if (!picked || !inspection) return;
    setSaving(true); setError(null);
    try {
      await adoptEmailTemplate({ ...picked, name: name.trim(), confirm: true,
        expectedSgVersionId: inspection.activeVersionId });
      onAdopted('Adopted. Nothing changed in SendGrid.');
    } catch (e) {
      const err = toTemplateApiError(e, 'The template could not be adopted.');
      setError(err.code === 'changed_since_inspection' ? `${err.error}` : err.error);
      if (err.code === 'changed_since_inspection') setInspection(null);
    } finally {
      setSaving(false);
    }
  };

  const keyReady = setup.templatesKeyConfigured;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="lg" fullWidth
            PaperProps={{ sx: { maxHeight: '94vh' } }}>
      <DialogTitle>Adopt a template SIMS already uses</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
          Only templates that {SCREEN[purpose].adoptFrom} are listed, and never another system's. Adopting records the
          template in SIMS as a {PURPOSE_LABEL[purpose].toLowerCase()} so it can be edited here; it changes nothing in
          SendGrid.
        </Typography>
        {!keyReady && (
          <Alert severity="info">Email template editing is not set up yet, so templates cannot be inspected or adopted.</Alert>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {loading && <Box sx={{ textAlign: 'center', p: 2 }}><CircularProgress size={28} /></Box>}
        {candidates && candidates.length === 0 && (
          <Alert severity="success">
            There is nothing to adopt: every {SCREEN[purpose].noun} SIMS uses is already managed here.
          </Alert>
        )}
        {candidates && candidates.length > 0 && (
          <TableContainer component={Paper} variant="outlined" sx={SHRINKS}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={HEAD}>SendGrid id (not managed by SIMS)</TableCell>
                  <TableCell sx={HEAD}>Used by</TableCell>
                  <TableCell sx={HEAD} align="right">Inspect</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {candidates.map((c) => (
                  <TableRow key={c.sgTemplateId} selected={picked?.sgTemplateId === c.sgTemplateId}>
                    <TableCell sx={CELL}><IdCell id={c.sgTemplateId} /></TableCell>
                    <TableCell sx={CELL}>
                      {[c.referencedBy.setting ? `the ${c.referencedBy.setting} setting` : '',
                        `${c.referencedBy.campaigns} campaign${c.referencedBy.campaigns === 1 ? '' : 's'}`,
                        `${c.referencedBy.dripEntries} drip entr${c.referencedBy.dripEntries === 1 ? 'y' : 'ies'}`]
                        .filter(Boolean).join(' · ')}
                    </TableCell>
                    <TableCell sx={CELL} align="right">
                      <Button size="small" variant="outlined" disabled={!keyReady || inspecting || saving}
                              onClick={() => void inspect(c.sgTemplateId, purpose)}>
                        Inspect as {PURPOSE_LABEL[purpose].toLowerCase()}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {inspecting && <Box sx={{ textAlign: 'center', p: 2 }}><CircularProgress size={28} /></Box>}
        {picked && inspection && (
          <Paper variant="outlined" sx={{ ...SHRINKS, p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="subtitle1" sx={{ color: 'var(--text-primary)', fontWeight: 600, overflowWrap: 'anywhere' }}>
              SendGrid name: {inspection.sgName || '—'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'var(--text-secondary)', overflowWrap: 'anywhere' }}>
              <span style={MONO}>{picked.sgTemplateId}</span> · {PURPOSE_LABEL[picked.purpose]} ·{' '}
              {inspection.versionCount} version{inspection.versionCount === 1 ? '' : 's'} in SendGrid · live version
              edited {formatWhen(inspection.activeUpdatedAt)}
              {inspection.editor ? ` in the ${inspection.editor} editor` : ''} · HTML {formatBytes(inspection.sizes.htmlBytes)}
            </Typography>
            <Typography variant="body2" sx={{ color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>
              <strong>Subject as written:</strong> {inspection.subject || '—'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>{describeUsage(inspection.usage)}</Typography>
            <Alert severity="warning">
              {inspection.warning || 'Publishing changes this template for every system that sends with it, not only SIMS.'}
              {inspection.editorNote ? ` ${inspection.editorNote}` : ''}
            </Alert>
            {inspection.issues.length > 0 ? (
              <Alert severity="info">
                Adoption is still allowed: the live template keeps working as it is. The first publish from SIMS must fix:
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {inspection.issues.slice(0, 15).map((i, n) => (
                    <li key={n}>{i.name ? `${i.name}: ` : ''}{i.message}{i.line ? ` (line ${i.line})` : ''}</li>
                  ))}
                </Box>
              </Alert>
            ) : (
              <Alert severity="success">It already meets every SIMS rule.</Alert>
            )}
            {inspection.previewDocument && (
              <Box sx={{ backgroundColor: '#e9edf3', borderRadius: 1, p: 1, display: 'flex', justifyContent: 'center' }}>
                <Box
                  component="iframe" title="Template to adopt" sandbox="" referrerPolicy="no-referrer"
                  srcDoc={inspection.previewDocument}
                  sx={{ width: 620, maxWidth: '100%', height: 480, [ULTRAWIDE]: { height: 640 }, border: 0,
                    display: 'block', backgroundColor: '#ffffff' }}
                />
              </Box>
            )}
            <TextField
              label="Name in SIMS" value={name} onChange={(e) => setName(e.target.value)}
              inputProps={{ maxLength: 75 }} sx={{ maxWidth: 560 }}
              helperText={`${name.trim().length}/75. SendGrid's own name is kept as it is; this one is for SIMS. `
                + 'Never a person\'s name or address.'}
            />
          </Paper>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Close</Button>
        <Button variant="contained" startIcon={<DownloadIcon />} onClick={() => void adopt()}
                disabled={!picked || !inspection || !name.trim() || saving || !keyReady}
                sx={{ backgroundColor: 'var(--accent)' }}>
          {saving ? 'Adopting…' : 'Adopt (changes nothing in SendGrid)'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── the screen ──────────────────────────────────────────────────────────────────────────────────

export default function EmailTemplates({ purpose }: {
  /** The one purpose this screen lists, creates and adopts. */
  purpose: ScreenPurpose;
}) {
  const user = getCurrentUser();
  const roles = useMemo(() => getUserRoles(user), [user?.email]); // eslint-disable-line react-hooks/exhaustive-deps
  const callerEmail: string = (user?.email as string) || '';
  const admin = isTemplateAdmin(roles);
  const screen = SCREEN[purpose];

  const [catalog, setCatalog] = useState<TemplateCatalog | null>(null);
  const [templates, setTemplates] = useState<TemplatePublic[]>([]);
  const [setup, setSetup] = useState<SetupState>({ templatesKeyConfigured: false, mailKeyConfigured: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [editor, setEditor] = useState<{ open: boolean; id: string | null }>({ open: false, id: null });
  const [healthOpen, setHealthOpen] = useState(false);
  const [adoptOpen, setAdoptOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cat, list] = await Promise.all([
        getTemplateCatalog(), listEmailTemplates({ purpose, archived: showArchived }),
      ]);
      // Only this screen's purpose reaches the editor, so its "Used for" picker and placeholders
      // offer nothing else. The server lists only the purpose it was asked for; the filter below is
      // a second check, not the gate.
      const own = cat.purposes?.[purpose];
      setCatalog({ ...cat, purposes: own ? { [purpose]: own } : {} });
      setTemplates(list.templates.filter((t) => t.purpose === purpose));
      setSetup(list.setup ?? cat.setup);
      setError(null);
    } catch (e) {
      // Never a blank tab: a missing container, a missing role and an outage read differently.
      setTemplates([]);
      setError(toTemplateApiError(e, 'The email templates could not be loaded.').error);
    } finally {
      setLoading(false);
    }
  }, [purpose, showArchived]);

  useEffect(() => { void load(); }, [load]);

  // False when the catalog came back without this purpose: the caller cannot write it.
  const purposeReady = !!catalog?.purposes?.[purpose];

  return (
    <Box sx={{ p: 3, [ULTRAWIDE]: { px: 5 } }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ ...SHRINKS, flex: '1 1 480px' }}>
          <Typography variant="h5" sx={{ color: 'var(--text-primary)' }}>{screen.heading}</Typography>
          <Typography variant="body2" sx={INTRO}>
            {screen.intro} Write one here, preview it with sample data, and publish: every
            publish is a new version in SendGrid, and the one before stays for roll back. Changes made in the
            SendGrid console are noticed and saved in history before anything is replaced. The SendGrid account is
            shared with other college systems, so templates SIMS does not manage are not listed here.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {admin && (
            <>
              <Button variant="outlined" startIcon={<HealthAndSafetyIcon />} onClick={() => setHealthOpen(true)}>
                Check SendGrid
              </Button>
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => setAdoptOpen(true)}>
                Adopt existing
              </Button>
            </>
          )}
          <Button variant="outlined" startIcon={<RefreshIcon />} disabled={loading} onClick={() => void load()}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<AddCircleIcon />} disabled={!purposeReady}
                  onClick={() => setEditor({ open: true, id: null })}
                  sx={{ backgroundColor: 'var(--accent)' }}>
            New template
          </Button>
        </Box>
      </Box>

      {!loading && !error && !setup.templatesKeyConfigured && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Email template editing is not set up yet. An administrator must add the SendGrid template key. Until then
          templates can be written, checked and previewed, but not created or published.
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
        <Box sx={{ flex: 1 }} />
        <FormControlLabel
          control={<Switch size="small" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />}
          label={<Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>Show archived</Typography>}
        />
      </Box>

      {loading && (
        <Box sx={{ textAlign: 'center', p: 4 }}><CircularProgress /></Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 2 }} action={
          <Button color="inherit" size="small" onClick={() => void load()}>Try again</Button>
        }>
          {error}
        </Alert>
      )}

      {!loading && !error && templates.length === 0 && (
        <Alert severity="info">
          {`No ${screen.noun}s yet. Press New template to write one`
            + `${admin ? `, or Adopt existing to bring in a ${screen.noun} SIMS already sends with` : ''}.`}
        </Alert>
      )}

      {!loading && !error && templates.length > 0 && (
        <TableContainer component={Paper} sx={SHRINKS}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={HEAD}>Name</TableCell>
                <TableCell sx={HEAD}>Status</TableCell>
                <TableCell sx={HEAD}>SendGrid</TableCell>
                <TableCell sx={HEAD} align="center">Live</TableCell>
                <TableCell sx={HEAD}>SendGrid id</TableCell>
                <TableCell sx={HEAD}>Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((t) => {
                const sync = syncLabel(t);
                return (
                  <TableRow key={t.id} hover sx={{ cursor: 'pointer' }}
                            onClick={() => setEditor({ open: true, id: t.id })}>
                    <TableCell sx={{ ...CELL, color: 'var(--text-primary)' }}>
                      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap', ...SHRINKS }}>
                        <span>{t.name || '—'}</span>
                        {t.origin === 'adopted' && <Chip size="small" variant="outlined" label="Adopted" />}
                        {t.isDripDefault && <Chip size="small" variant="outlined" color="info" label="Drip default" />}
                        {t.needsFix && <Chip size="small" color="warning" variant="outlined" label="Needs fix" />}
                      </Box>
                      {t.subject && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)', overflowWrap: 'anywhere' }}>
                          {t.subject}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={CELL}>
                      <Chip size="small" label={statusLabel(t)} color={STATUS_COLOR[t.status] ?? 'default'} />
                    </TableCell>
                    <TableCell sx={CELL}>
                      <Tooltip title={sync.help}><Chip size="small" variant="outlined" label={sync.label} color={sync.color} /></Tooltip>
                    </TableCell>
                    <TableCell sx={CELL} align="center">
                      {/* During drift SendGrid sends the console's version, not SIMS's last publish. */}
                      {t.syncState === 'drift' && t.drift?.versionNo != null && t.drift.versionNo !== t.activeVersionNo ? (
                        <Tooltip title={`SendGrid is sending v${t.drift.versionNo}, from the SendGrid console. SIMS's last `
                          + `publish is v${t.activeVersionNo ?? '?'}.`}>
                          <span>v{t.drift.versionNo} (console)</span>
                        </Tooltip>
                      ) : t.activeVersionNo ? `v${t.activeVersionNo}` : '—'}
                    </TableCell>
                    <TableCell sx={CELL}><IdCell id={t.sgTemplateId} /></TableCell>
                    <TableCell sx={{ ...CELL, whiteSpace: 'nowrap', overflowWrap: 'normal' }}>
                      {formatWhen(t.updatedAt)}
                      {t.updatedBy && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)' }}>
                          {t.updatedBy}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <EmailTemplateEditor
        open={editor.open}
        templateId={editor.id}
        initialPurpose={purpose}
        catalog={catalog}
        setup={setup}
        roles={roles}
        callerEmail={callerEmail}
        onClose={() => { setEditor((e) => ({ ...e, open: false })); void load(); }}
        onChanged={(message) => { setToast(message); void load(); }}
      />
      {admin && <HealthDialog open={healthOpen} onClose={() => setHealthOpen(false)} />}
      {admin && (
        <AdoptDialog open={adoptOpen} purpose={purpose} setup={setup} onClose={() => setAdoptOpen(false)}
                     onAdopted={(message) => { setAdoptOpen(false); setToast(message); void load(); }} />
      )}
      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)}
                message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}
